import { Link } from "react-router-dom";
import { ShieldCheck, Lock, Fingerprint, Prohibit, Eye, Trash, Certificate, WarningCircle } from "@phosphor-icons/react";
import BuzzLogo from "../components/BuzzLogo";

const items = [
  {
    icon: Lock,
    title: "Passwords hashed with bcrypt",
    body: "Every operator password is stored as a bcrypt hash with a random per-account salt. We literally can't read your password — not from the database, not from a subpoena, not from a rogue employee. Reset is the only recovery path.",
  },
  {
    icon: ShieldCheck,
    title: "TLS 1.2+ end-to-end",
    body: "Every byte between your browser, your guests' phones, our servers, Twilio, and OpenAI travels over TLS. HSTS is enforced with a 1-year policy, so downgrade attacks fail before they start.",
  },
  {
    icon: Certificate,
    title: "Twilio webhooks are cryptographically verified",
    body: "The moment you connect your Twilio number, every inbound call and SMS is validated against Twilio's HMAC-SHA1 signature using your own auth token. Anyone spoofing a Twilio webhook to fake calls, add fake guests, or run up your OpenAI bill gets a 403 — silently blocked at the door.",
  },
  {
    icon: Fingerprint,
    title: "Multi-tenant isolation, verified at the query layer",
    body: "Every waitlist read, call log, guest lookup, appointment fetch, and analytics query is scoped by your business ID from your signed JWT. It is impossible — by design and by test — for one restaurant to see another restaurant's data. No shared caches, no cross-tenant admin panels.",
  },
  {
    icon: Prohibit,
    title: "Card numbers never touch our servers",
    body: "All billing flows through Stripe's Checkout & PaymentElement. We store a customer ID and a subscription status. Full card numbers, CVCs, and bank details land inside Stripe's PCI Level 1 vault — not ours.",
  },
  {
    icon: Eye,
    title: "PII scrubbed before it ever hits disk",
    body: "Credit cards, SSNs, dates of birth, and other sensitive strings are detected and redacted from voice + chat transcripts BEFORE the record is written to our database. The raw string never touches storage. Tested against 7+ real-world spoken variants.",
  },
  {
    icon: WarningCircle,
    title: "Brute-force protection on sign-in",
    body: "5 failed sign-in attempts within 15 minutes and the account is locked for the rest of the window. We track by account (not IP) so an attacker rotating IPs still hits the wall. Every login attempt is timestamped in an audit log.",
  },
  {
    icon: ShieldCheck,
    title: "Manager PIN gate on destructive actions",
    body: "Turning off the AI receptionist, editing manager phone numbers, or entering admin settings all require a 4-8 digit manager PIN with its own rate limiter. Any staff member with dashboard access still can't sabotage the shift without knowing the PIN.",
  },
  {
    icon: Trash,
    title: "Automatic transcript retention",
    body: "Call and chat transcripts are retained for 90 days and then automatically purged by a background worker. You can request full data export or immediate deletion in Settings — we act on both within 30 days as required by CCPA/GDPR.",
  },
  {
    icon: Lock,
    title: "Security response headers on every route",
    body: "Every API and page response ships with HSTS, X-Frame-Options: DENY (no clickjacking), X-Content-Type-Options: nosniff, strict Referrer-Policy, and a locked-down Permissions-Policy (no geolocation, camera, mic, or payment API from cross-origin frames).",
  },
];

const Nav = () => (
  <header className="fixed top-0 left-0 right-0 z-40 glass">
    <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-[#1E3A2F] flex items-center justify-center">
          <BuzzLogo size={22} />
        </div>
        <span className="font-heading font-extrabold text-lg tracking-tight">BuzzerAI</span>
      </Link>
      <div className="flex items-center gap-3">
        <Link to="/privacy" className="text-sm text-[#5C6B61]" data-testid="trust-nav-privacy">Privacy</Link>
        <Link to="/terms" className="text-sm text-[#5C6B61]" data-testid="trust-nav-terms">Terms</Link>
        <Link to="/signup" className="btn-primary text-sm" data-testid="trust-nav-signup">Start free</Link>
      </div>
    </div>
  </header>
);

export default function Trust() {
  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="trust-page">
      <Nav />

      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="overline mb-4 text-[#D96C5B]">Security &amp; Trust</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6">
            Your guests trust you. <span className="text-[#D96C5B]">We take that seriously.</span>
          </h1>
          <p className="text-lg text-[#5C6B61] leading-relaxed max-w-2xl mb-4">
            BuzzerAI runs the front door for your business — every phone call, every waitlist entry, every text message. That's a lot of trust. Here's exactly what we do to be worthy of it.
          </p>
          <p className="text-sm text-[#5C6B61] leading-relaxed max-w-2xl">
            No marketing puffery. Everything on this page is enforced in code and testable against our live API. Have a security question we haven't answered? Email <a href="mailto:security@buzzerai.app" className="underline text-[#1E3A2F] font-semibold">security@buzzerai.app</a>.
          </p>
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-5" data-testid="trust-items">
          {items.map((it) => {
            const I = it.icon;
            return (
              <div key={it.title} className="paper-card p-6 fade-up" data-testid={`trust-item-${it.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>
                <div className="w-11 h-11 rounded-lg bg-[#1E3A2F] flex items-center justify-center mb-4">
                  <I size={22} color="#F5C543" weight="fill" />
                </div>
                <h3 className="text-lg font-heading font-extrabold text-[#121914] mb-2">{it.title}</h3>
                <p className="text-sm text-[#5C6B61] leading-relaxed">{it.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="max-w-4xl mx-auto paper-card p-8 bg-[#1E3A2F] text-[#F7F6F2]" data-testid="trust-cta">
          <div className="overline text-[#F5C543] mb-3">Report a vulnerability</div>
          <h2 className="text-2xl md:text-3xl font-heading font-extrabold mb-3">Found a security issue? Tell us.</h2>
          <p className="text-sm text-[#E1DFD5] leading-relaxed mb-4 max-w-2xl">
            We treat security reports as top-priority tickets. Email <a href="mailto:security@buzzerai.app" className="underline font-semibold text-[#F5C543]">security@buzzerai.app</a> with reproduction steps. We commit to acknowledging within 24 hours and — for critical issues — a hotfix within 72 hours of confirmation.
          </p>
          <p className="text-xs text-[#C2D1C1] leading-relaxed">
            Please don't run automated scans against our production servers, and never test with real guest data. Coordinated disclosure gets you credit on this page (if you want it) and a written thank-you.
          </p>
        </div>
      </section>

      <footer className="bg-[#1E3A2F] text-white px-6 py-16">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
              <BuzzLogo size={22} />
            </div>
            <span className="font-heading font-extrabold text-lg">BuzzerAI</span>
          </div>
          <div className="flex flex-wrap gap-6 text-sm text-white/80">
            <Link to="/">Home</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/faq">FAQ</Link>
            <Link to="/troubleshooting">Troubleshooting</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
